/******************************************************************************
 * @Author                : Jbristhuille<jbristhuille@gmail.com>              *
 * @CreatedDate           : 2025-02-05 16:48:52                               *
 * @LastEditors           : Jbristhuille<jbristhuille@gmail.com>              *
 * @LastEditDate          : 2025-02-10 17:21:33                               *
 *****************************************************************************/

/******************************************************************************
 * @Author                : Jbristhuille<jbristhuille@gmail.com>              *
 * @CreatedDate           : 2025-02-05 16:47:42                               *
 * @LastEditors           : Jbristhuille<jbristhuille@gmail.com>              *
 * @LastEditDate          : 2025-02-05 16:48:04                               *
 *****************************************************************************/

/* SUMMARY
 * Imports
 * Components
 * Services
 * AdminCompinfos: Admin compinfos view
 * useEffect: get compinfos list
 * getInputCompInfos: Get compinfos input list
 * getSelectCompInfos: Get compinfos select list
 * mergeCompInfos: Merge input and select compinfos
 */

/* Imports */
import { Typography, Button } from "@material-tailwind/react";
import { useEffect, useState } from "react";
import { t } from "i18next";
/***/

/* Components */
/***/

/* Services */
import { api } from "../../../../services/api/api";
import { TabsNav } from "../../../../components/tabs/TabsNav";
import { TruckIcon } from "@heroicons/react/24/solid";
import Compinfos from "./tabs/CompInfos";
import { createListQuery, searchWithFilters } from "../../../../services/api/search";
import AdminInterventionsTypesTab from "./tabs/interventions-types/AdminInterventionsTypesTab";
import AdminInterventionsOriginsTab from "./tabs/interventions-origins/AdminInterventionsOriginsTab";
/***/

/**
 * AdminCompinfos: Admin compinfos view
 */
export default function AdminCompinfos() {
  // États
  const [activeTab, setActiveTab] = useState("types");

  const [compInfos, setCompInfos] = useState<EasyTech.CompInfo[] | null>(null);
  const [isReordering, setIsReordering] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleTabChange = (tabValue: string) => {
    setActiveTab(tabValue);
  };

  /**
   * getInputCompInfos: Get compinfos input list
   * @return - Input compinfos list
   */
  const getInputCompInfos = (): Promise<EasyTech.CompInfo[]> => {
    return new Promise((resolve) => {
      let ret: EasyTech.CompInfo[] = [];


      searchWithFilters<EasyTech.CompInfo>("compinfos/inputs", {
        queries: [createListQuery("enabled", [true, false], "IN")]
      }, true, true)
        .then((res) => (ret = res.data as EasyTech.CompInfo[]))
        .catch((err) => console.error(err))
        .finally(() => resolve(ret));
    });
  };
  /***/

  /**
   * getSelectCompInfos: Get compinfos select list
   * @return - Select compinfos list
   */
  const getSelectCompInfos = (): Promise<EasyTech.CompInfo[]> => {
    return new Promise((resolve) => {
      let ret: EasyTech.CompInfo[] = [];

      searchWithFilters<EasyTech.CompInfo>("compinfos/selects", {
        queries: [createListQuery("enabled", [true, false], "IN")]
      }, true, true)
        .then((res) => (ret = res.data as EasyTech.CompInfo[]))
        .catch((err) => console.error(err))
        .finally(() => resolve(ret));
    });
  };
  /***/

  /**
   * mergeCompInfos: Merge input and select compinfos
   */
  const mergeCompInfos = async () => {
    const inputCompInfos = await getInputCompInfos();
    const selectCompInfos = await getSelectCompInfos();

    const compInfos = [...inputCompInfos, ...selectCompInfos];
    // Tri des compInfos par order
    const sortedCompInfos = compInfos.filter((e) => e.service === activeTab).sort((a, b) => {
      // Si order n'est pas défini, on met l'élément à la fin
      if (a.order === undefined) return 1;
      if (b.order === undefined) return -1;
      return a.order - b.order;
    });
    setCompInfos(sortedCompInfos);
  };
  /***/

  /**
   * toggleReordering: Active/désactive le mode de réordonnancement
   */
  const toggleReordering = () => {
    setIsReordering(!isReordering);
  };
  /***/

  /**
   * saveNewOrder: Sauvegarde le nouvel ordre 
   */
  const saveNewOrder = async () => {
    if (!compInfos) return;

    setIsSaving(true);

    try {
      // Formatage des données pour l'API
      const inputs: { id: string, order: number }[] = [];
      const selects: { id: string, order: number }[] = [];

      // Classification des compInfos en inputs et selects et assignation de l'ordre
      compInfos.forEach((compInfo, index) => {
        const orderData = { id: compInfo.id, order: index + 1 };

        if (compInfo.type === "SELECT" || compInfo.type === "MULTIPLESELECT" ||
          compInfo.type === "RADIO" || compInfo.type === "CHECKBOX") {
          selects.push(orderData);
        } else {
          inputs.push(orderData);
        }
      });

      await api.post("admin/compinfos/reorder", { inputs, selects });

      // Afficher le nouvel ordre
      await mergeCompInfos();
      setIsReordering(false);

    } catch (error) {
      console.error("Erreur lors de la sauvegarde de l'ordre :", error);
    } finally {
      setIsSaving(false);
    }
  };
  /***/


  /**
   * useEffect: get compinfos list
   */
  useEffect(() => {
    setCompInfos(null);
    mergeCompInfos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);
  /***/

  const tabs = [
    {
      label: t("admin:compinfos:tabs:types"),
      value: "types",
      icon: TruckIcon,
      Component: AdminInterventionsTypesTab,
    },
    {
      label: t("admin:compinfos:tabs:origins"),
      value: "origins",
      icon: TruckIcon,
      Component: AdminInterventionsOriginsTab,
    },
    {
      label: t("admin:compinfos:tabs:workorder"),
      value: "WORKORDER",
      icon: TruckIcon,
      Component: <Compinfos compInfos={compInfos} onCompInfosChange={setCompInfos} isReordering={isReordering} onChange={mergeCompInfos} />,
    }
  ];

  return (
    <div className="views-padded">
      <div className="flex justify-between items-center my-4">
        <Typography variant="h4">{t("admin:compinfos:title")}</Typography>

        {activeTab === "WORKORDER" && (
          isReordering ? (
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outlined"
                color="gray"
                className="normal-case font-thin"
                onClick={toggleReordering}
                disabled={isSaving}
              >
                {t("common:cancel")}
              </Button>
              <Button
                size="sm"
                variant="filled"
                color="blue"
                className="normal-case font-thin"
                onClick={saveNewOrder}
                disabled={isSaving}
              >
                {isSaving ? t("admin:buttons:saving") : t("admin:buttons:saveOrder")}
              </Button>
            </div>
          ) : (
            <Button
              variant="filled"
              color="blue"
              className="normal-case font-thin"
              onClick={toggleReordering}
            >
              {t("common:reorder")}
            </Button>
          )
        )}
      </div>

      <TabsNav
        defaultTab={activeTab}
        tabs={tabs}
        data={compInfos}
        onTabChange={handleTabChange}
      />
    </div>
  );
}
/***/
